import React, { useEffect, useState } from 'react';
import { Table, Button, Input, Space, Modal, message, Tooltip, Popconfirm } from 'antd';
import { SearchOutlined, EditOutlined, DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import CollectionService from '../api/CollectionService';
import Highlighter from 'react-highlight-words';

const CollectionList = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const navigate = useNavigate();

  const fetchCollections = async () => {
    try {
      setLoading(true);
      const response = await CollectionService.getAllCollections();
      if (response.statusCode === 200) {
        // Add a key property to each collection for the Table component
        const collectionsWithKeys = response.data.map(collection => ({
          ...collection,
          key: collection._id
        }));
        
        // Handle brand information more safely
        const collectionsWithBrandInfo = await Promise.all(
          collectionsWithKeys.map(async (collection) => {
            // Only attempt to fetch brand info if brand is a string ID and not already an object
            if (collection.brand && typeof collection.brand === 'string') {
              try {
                const brandResponse = await CollectionService.getBrandById(collection.brand);
                
                // Handle different response structures
                if (brandResponse && brandResponse.statusCode === 200 && brandResponse.data) {
                  return {
                    ...collection,
                    brand: brandResponse.data
                  };
                } else if (brandResponse && brandResponse.data) {
                  return {
                    ...collection,
                    brand: brandResponse.data
                  };
                } else if (brandResponse && (brandResponse.name || brandResponse.brand_name)) {
                  // The response itself might be the brand object
                  return {
                    ...collection,
                    brand: brandResponse
                  };
                }
                
                // If we couldn't extract brand info, use a default
                return {
                  ...collection,
                  brand: { 
                    name: `Brand ${collection.brand.substring(0, 6)}...`, 
                    _id: collection.brand 
                  }
                };
              } catch (err) {
                // Silently fail and use a default brand object
                return {
                  ...collection,
                  brand: { 
                    name: `Brand ${collection.brand.substring(0, 6)}...`, 
                    _id: collection.brand 
                  }
                };
              }
            }
            // If brand is already an object or doesn't exist, just return the collection as is
            return collection;
          })
        );
        
        setCollections(collectionsWithBrandInfo);
      } else {
        message.error('Failed to fetch collections');
      }
    } catch (error) {
      message.error('Failed to fetch collections');
      console.error('Error fetching collections:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, []);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, backgroundColor: "#e67929", border: 'none' }}
          >
            Search
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, record) =>
      record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const response = await CollectionService.deleteCollection(id);
      if (response.statusCode === 200) {
        message.success('Collection deleted successfully');
        fetchCollections();
      } else {
        message.error(response.message || 'Failed to delete collection');
      }
    } catch (error) {
      message.error('Failed to delete collection: ' + (error.message || 'Unknown error'));
      console.error('Error deleting collection:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBrandFilters = () => {
    return collections
      .map(collection => {
        // Extract brand name considering all possible data structures
        let brandName = null;
        
        if (collection.brand) {
          if (typeof collection.brand === 'object') {
            // Handle array of brands
            if (Array.isArray(collection.brand) && collection.brand.length > 0) {
              brandName = collection.brand[0].brand_name || collection.brand[0].name;
            }
            // Handle direct brand properties
            else if (collection.brand.brand_name) {
              brandName = collection.brand.brand_name;
            } 
            else if (collection.brand.name) {
              brandName = collection.brand.name;
            }
            // Handle nested data structure
            else if (collection.brand.data) {
              if (collection.brand.data.brand_name) {
                brandName = collection.brand.data.brand_name;
              } else if (collection.brand.data.name) {
                brandName = collection.brand.data.name;
              }
              // Handle array in data
              else if (Array.isArray(collection.brand.data) && collection.brand.data.length > 0) {
                brandName = collection.brand.data[0].brand_name || collection.brand.data[0].name;
              }
            }
          }
        }
        
        return brandName ? { text: brandName, value: brandName } : null;
      })
      .filter(Boolean) // Remove nulls
      .filter((brand, index, self) => 
        // Remove duplicates
        index === self.findIndex(b => b.value === brand.value)
      )
      .sort((a, b) => a.text.localeCompare(b.text)); // Sort alphabetically
  };

  const columns = [
    {
      title: 'Collection Name',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps('name'),
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      ...getColumnSearchProps('slug'),
    },
    {
      title: 'Brand',
      dataIndex: 'brand',
      key: 'brand',
      render: (brand, record) => {
        // Check for different possible structures of brand data
        if (!brand) {
          return <span className="text-muted">No Brand</span>;
        }
        
        if (typeof brand === 'object') {
          // Handle the case where brand is an array (from backend API)
          if (Array.isArray(brand) && brand.length > 0) {
            return brand[0].brand_name || brand[0].name || `Brand ${brand[0]._id.substring(0, 8)}...`;
          }
          
          // Handle the case where brand is a regular object
          if (brand.brand_name) {
            return brand.brand_name;
          }
          
          if (brand.name) {
            return brand.name;
          }
          
          // Handle nested data structure
          if (brand.data) {
            if (brand.data.brand_name) return brand.data.brand_name;
            if (brand.data.name) return brand.data.name;
            
            // Handle array in data
            if (Array.isArray(brand.data) && brand.data.length > 0) {
              return brand.data[0].brand_name || brand.data[0].name || `Brand ${brand.data[0]._id.substring(0, 8)}...`;
            }
          }
          
          const brandId = brand._id || (typeof record.brand === 'string' ? record.brand : 'Unknown');
          return <span className="text-muted">Brand ID: {brandId.substring(0, 8)}...</span>;
        }
        
        if (typeof brand === 'string') {
          // If still a string ID, show a placeholder
          return <span className="text-muted">Brand ID: {brand.substring(0, 8)}...</span>;
        }
        
        // Fallback for any other unexpected formats
        return <span className="text-muted">Unknown Format</span>;
      },
      filters: getBrandFilters(),
      onFilter: (value, record) => {
        if (!record.brand) return false;
        
        if (typeof record.brand === 'object') {
          // Handle array of brands
          if (Array.isArray(record.brand) && record.brand.length > 0) {
            return (record.brand[0].brand_name === value || record.brand[0].name === value);
          }
          
          // Handle direct brand properties
          if (record.brand.brand_name === value) return true;
          if (record.brand.name === value) return true;
          
          // Handle nested data structure
          if (record.brand.data) {
            if (record.brand.data.brand_name === value) return true;
            if (record.brand.data.name === value) return true;
            
            // Handle array in data
            if (Array.isArray(record.brand.data) && record.brand.data.length > 0) {
              return (record.brand.data[0].brand_name === value || record.brand.data[0].name === value);
            }
          }
        }
        
        return false;
      },
    },
    {
      title: 'Has Description',
      dataIndex: 'description',
      key: 'hasDescription',
      render: (description) => description ? <span style={{color: 'green'}}>Yes</span> : <span style={{color: 'red'}}>No</span>,
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false }
      ],
      onFilter: (value, record) => !!record.description === value,
      width: '120px'
    },
    {
      title: 'Has Meta Description',
      dataIndex: 'meta_description',
      key: 'hasMetaDescription',
      render: (metaDescription) => metaDescription ? <span style={{color: 'green'}}>Yes</span> : <span style={{color: 'red'}}>No</span>,
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false }
      ],
      onFilter: (value, record) => !!record.meta_description === value,
      width: '140px'
    },
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      render: (image) =>
        image ? (
          <img
            src={image}
            alt="Collection"
            style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '5px' }}
          />
        ) : (
          <div style={{ 
            width: '60px', 
            height: '60px', 
            backgroundColor: '#f0f0f0', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            borderRadius: '5px',
            color: '#999'
          }}>
            No Image
          </div>
        ),
      filters: [
        { text: 'Has Image', value: true },
        { text: 'No Image', value: false }
      ],
      onFilter: (value, record) => (!!record.image) === value,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/admin/collections/edit/${record._id}`)}
              style={{ backgroundColor: "#198754", border: 'none' }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Are you sure you want to delete this collection?"
              onConfirm={() => handleDelete(record._id)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ style: { backgroundColor: '#e67929', border: 'none' } }}
            >
              <Button type="primary" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const customStyles = {
    rows: {
      style: {
        minHeight: "50px",
        backgroundColor: "white",
        marginTop: "7px",
        boxShadow: " 0px 2px 2px #ced4da",
        transition: "background-color 0.3s",
        "&:hover": {
          backgroundColor: "#f0f0f0",
          cursor: "pointer",
        },
      },
    },
    headCells: {
      style: {
        backgroundColor: "rgb(230, 121, 41)",
        fontSize: "15px",
        color: "white",
        fontWeight: "600",
        boxShadow: "0 2px 2px 0 #ced4da, 0 2px 4px 0 rgba(0, 0, 0, 0.19)",
      },
    },
    scrollbar: {
      width: "18px",
      track: {
        backgroundColor: "#f5f5f5",
        borderRadius: "8px",
      },
      thumb: {
        backgroundColor: "#888",
        borderRadius: "8px",
      },
    },
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Collections</h1>
        <Space>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={fetchCollections}
            loading={loading}
            style={{ backgroundColor: "#e67929", borderRadius: "10px", border: 'none' }}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/admin/collections/create')}
            style={{ backgroundColor: "#e67929", borderRadius: "10px", border: 'none' }}
          >
            Add Collection
          </Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={collections}
        rowKey="_id"
        loading={loading}
        pagination={{ 
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} collections`
        }}
        style={{ borderRadius: '10px', overflow: 'hidden' }}
        bordered
        size="middle"
        scroll={{ x: 1200 }}
        expandable={{
          expandedRowRender: record => (
            <div style={{ padding: 10 }}>
              <p><strong>Description:</strong> {record.description || 'No description provided'}</p>
              <p><strong>Meta Description:</strong> {record.meta_description || 'No meta description provided'}</p>
              <p><strong>Created:</strong> {new Date(record.createdAt).toLocaleString()}</p>
              <p><strong>Last Updated:</strong> {new Date(record.updatedAt).toLocaleString()}</p>
            </div>
          ),
        }}
      />
    </div>
  );
};

export default CollectionList;
